import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { endEbayListingsForItems } from '@/lib/endEbayListingsForItems';

export interface Transaction {
  id: string;
  item_id: string;
  seller_id: string;
  buyer_id: string | null;
  amount_cents: number | null;
  status: 'pending' | 'completed' | 'cancelled';
  completed_at: string | null;
  created_at: string;
  transaction_type?: 'sale' | 'trade';
  trade_cash_received_cents?: number | null;
  trade_cash_paid_cents?: number | null;
  trade_notes?: string | null;
  payment_method?: string | null;
  sale_photo_url?: string | null;
  // Tax snapshot fields
  cost_basis_cents_snapshot?: number | null;
  acquisition_date_snapshot?: string | null;
  holding_period?: 'short_term' | 'long_term' | null;
  realized_gain_cents?: number | null;
}

export interface TradeItemInput {
  direction: 'received' | 'gave';
  itemId?: string; // For items from inventory
  externalTitle?: string; // For external items
  estimatedValueCents?: number;
  // Fields for creating inventory from received items
  addToInventory?: boolean;
  category?: string;
  condition?: string;
  game?: string;
}

export interface Review {
  id: string;
  transaction_id: string;
  reviewer_id: string;
  reviewed_user_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
}

interface MarkAsSoldInput {
  itemId: string;
  buyerId?: string;
  salePriceCents?: number;
  quantitySold?: number;
  paymentMethod?: string;
  salePhotoUrl?: string;
  saleDate?: Date;
}

interface RecordTradeInput {
  itemId: string; // The main item being traded away
  tradeItems: TradeItemInput[]; // All items involved in the trade
  cashReceivedCents?: number;
  cashPaidCents?: number;
  tradeNotes?: string;
  saleDate?: Date;
  tradePhotoUrl?: string;
}

export function useMarkAsSold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, buyerId, salePriceCents: salePriceCentsInput, quantitySold = 1, paymentMethod, salePhotoUrl, saleDate }: MarkAsSoldInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current item to check quantity and tax-relevant data (include individual costs array)
      const { data: item, error: fetchError } = await supabase
        .from('user_items')
        .select('quantity, cost_basis_cents, purchase_date, individual_costs_cents, individual_dates, asking_price_cents')
        .eq('id', itemId)
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;
      if (!item) throw new Error('Item not found');

      // No sale price given → assume it sold at its listed asking price.
      const salePriceCents =
        salePriceCentsInput && salePriceCentsInput > 0
          ? salePriceCentsInput
          : (item.asking_price_cents && item.asking_price_cents > 0
              ? item.asking_price_cents * quantitySold
              : undefined);

      const currentQuantity = item.quantity || 1;

      
      if (quantitySold > currentQuantity) {
        throw new Error(`Cannot sell ${quantitySold} units. Only ${currentQuantity} available.`);
      }

      const saleDateObj = saleDate || new Date();
      const saleDateIso = saleDateObj.toISOString();
      
      // Determine cost basis - use individual costs if available (FIFO: take first N costs)
      const individualCosts = item.individual_costs_cents as number[] | null;
      const individualDates = item.individual_dates as string[] | null;
      
      let costBasisSnapshot: number | null = null;
      let acquisitionDateSnapshot: string | null = null;
      
      if (individualCosts && individualCosts.length > 0) {
        // Sum the first N individual costs for the units being sold
        const costsForSale = individualCosts.slice(0, quantitySold);
        costBasisSnapshot = costsForSale.reduce((sum, c) => sum + (c || 0), 0);
        // Use earliest date from sold units for holding period calculation
        if (individualDates && individualDates.length > 0) {
          const datesForSale = individualDates.slice(0, quantitySold).filter(Boolean);
          if (datesForSale.length > 0) {
            acquisitionDateSnapshot = datesForSale.sort()[0]; // Earliest date
          }
        }
      } else {
        // Fall back to average cost basis (multiply by quantity for total)
        costBasisSnapshot = item.cost_basis_cents ? item.cost_basis_cents * quantitySold : null;
        acquisitionDateSnapshot = item.purchase_date || null;
      }
      
      // Calculate holding period (>365 days = long_term)
      let holdingPeriod: 'short_term' | 'long_term' | null = null;
      if (acquisitionDateSnapshot) {
        const acquisitionDate = new Date(acquisitionDateSnapshot);
        const holdingDays = Math.floor(
          (saleDateObj.getTime() - acquisitionDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        holdingPeriod = holdingDays > 365 ? 'long_term' : 'short_term';
      }
      
      // Calculate realized gain/loss (sale price - total cost basis of sold units)
      const realizedGainCents = (salePriceCents !== undefined && costBasisSnapshot !== null)
        ? salePriceCents - costBasisSnapshot
        : null;
      
      // Create transaction record with tax snapshots
      const { data: transaction, error: txError } = await supabase
        .from('transactions')
        .insert({
          item_id: itemId,
          seller_id: user.id,
          buyer_id: buyerId || null,
          amount_cents: salePriceCents || null,
          status: 'completed',
          completed_at: saleDateIso,
          payment_method: paymentMethod || null,
          sale_photo_url: salePhotoUrl || null,
          // Tax snapshot fields
          quantity_sold: quantitySold,
          cost_basis_cents_snapshot: costBasisSnapshot,
          acquisition_date_snapshot: acquisitionDateSnapshot,
          holding_period: holdingPeriod,
          realized_gain_cents: realizedGainCents,
        })
        .select()
        .single();

      if (txError) throw txError;

      // If selling all units, mark as fully sold
      if (quantitySold >= currentQuantity) {
        const { error: itemError } = await supabase
          .from('user_items')
          .update({
            sold_at: saleDateIso,
            sold_to_user_id: buyerId || null,
            sold_price_cents: salePriceCents || null,
            visibility: 'private',
            is_listed: false,
            quantity: 0,
            // Clear individual arrays since all units are sold
            individual_costs_cents: null,
            individual_dates: null,
          })
          .eq('id', itemId)
          .eq('user_id', user.id);

        if (itemError) throw itemError;

        // Item is gone from inventory — pull it off eBay too (best-effort).
        await endEbayListingsForItems([itemId]);
      } else {
        // Partial sale - decrement quantity and remove sold units from individual arrays
        const remainingCosts = individualCosts ? individualCosts.slice(quantitySold) : null;
        const remainingDates = individualDates ? individualDates.slice(quantitySold) : null;
        
        // Recalculate average cost basis for remaining units
        const newAvgCostBasis = remainingCosts && remainingCosts.length > 0
          ? Math.round(remainingCosts.reduce((sum, c) => sum + (c || 0), 0) / remainingCosts.length)
          : item.cost_basis_cents;
        
        const { error: itemError } = await supabase
          .from('user_items')
          .update({
            quantity: currentQuantity - quantitySold,
            individual_costs_cents: remainingCosts && remainingCosts.length > 0 ? remainingCosts : null,
            individual_dates: remainingDates && remainingDates.length > 0 ? remainingDates : null,
            cost_basis_cents: newAvgCostBasis, // Update average for display
          })
          .eq('id', itemId)
          .eq('user_id', user.id);

        if (itemError) throw itemError;
      }

      return { transaction, quantitySold, remainingQuantity: Math.max(0, currentQuantity - quantitySold) };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-item'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['ebay-listing'] });
      queryClient.invalidateQueries({ queryKey: ['ebay-listings'] });
    },
  });
}

export function useRecordTrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      itemId, 
      tradeItems, 
      cashReceivedCents, 
      cashPaidCents, 
      tradeNotes,
      saleDate,
      tradePhotoUrl,
    }: RecordTradeInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the main item being traded (include purchase_date for tax calculations)
      const { data: item, error: fetchError } = await supabase
        .from('user_items')
        .select('quantity, cost_basis_cents, purchase_date')
        .eq('id', itemId)
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;
      if (!item) throw new Error('Item not found');

      const tradeDateObj = saleDate || new Date();
      const tradeDateIso = tradeDateObj.toISOString();

      // Calculate trade value from items received
      const receivedValue = tradeItems
        .filter(t => t.direction === 'received')
        .reduce((sum, t) => sum + (t.estimatedValueCents || 0), 0);
      const totalReceivedCents = receivedValue + (cashReceivedCents || 0);

      // Calculate tax-relevant fields for the trade
      const acquisitionDate = item.purchase_date ? new Date(item.purchase_date) : null;
      const holdingDays = acquisitionDate 
        ? Math.floor((tradeDateObj.getTime() - acquisitionDate.getTime()) / (1000 * 60 * 60 * 24))
        : null;
      const holdingPeriod = holdingDays !== null && holdingDays > 365 ? 'long_term' : 'short_term';
      
      // FMV received = total value of items received + cash received
      // Realized gain = FMV received - cost basis
      const realizedGainCents = totalReceivedCents - (item.cost_basis_cents || 0);

      // Create transaction record for the trade with tax snapshots
      const { data: transaction, error: txError } = await supabase
        .from('transactions')
        .insert({
          item_id: itemId,
          seller_id: user.id,
          buyer_id: null,
          amount_cents: totalReceivedCents || null,
          status: 'completed',
          completed_at: tradeDateIso,
          transaction_type: 'trade',
          trade_cash_received_cents: cashReceivedCents || null,
          trade_cash_paid_cents: cashPaidCents || null,
          trade_notes: tradeNotes || null,
          sale_photo_url: tradePhotoUrl || null,
          // Tax snapshot fields
          quantity_sold: 1,
          cost_basis_cents_snapshot: item.cost_basis_cents || null,
          acquisition_date_snapshot: item.purchase_date || null,
          holding_period: holdingPeriod,
          realized_gain_cents: realizedGainCents,
        })
        .select()
        .single();

      if (txError) throw txError;

      // Insert trade items
      if (tradeItems.length > 0) {
        const tradeItemsToInsert = tradeItems.map(ti => ({
          transaction_id: transaction.id,
          direction: ti.direction,
          item_id: ti.itemId || null,
          external_title: ti.externalTitle || null,
          external_estimated_value_cents: ti.estimatedValueCents || null,
        }));

        const { data: insertedTradeItems, error: tradeItemsError } = await supabase
          .from('trade_items')
          .insert(tradeItemsToInsert)
          .select();

        if (tradeItemsError) throw tradeItemsError;

        // Create inventory items for received items with addToInventory flag
        const itemsToAddToInventory = tradeItems.filter(
          ti => ti.direction === 'received' && ti.addToInventory && !ti.itemId
        );

        if (itemsToAddToInventory.length > 0) {
          const tradeDateOnly = tradeDateIso.split('T')[0];
          
          type ListingCategory = 'sealed' | 'singles' | 'slabs' | 'lots' | 'accessories';
          type ListingCondition = 'mint' | 'near_mint' | 'lightly_played' | 'moderately_played' | 'heavily_played' | 'damaged' | 'factory_sealed' | 'factory_sealed_minor' | 'factory_sealed_major' | 'resealed' | 'opened' | 'new' | 'like_new' | 'good' | 'fair';
          
          const newInventoryItems = itemsToAddToInventory.map(ti => ({
            user_id: user.id,
            title: ti.externalTitle || 'Trade Item',
            category: (ti.category || 'singles') as ListingCategory,
            condition: (ti.condition || 'near_mint') as ListingCondition,
            game: ti.game || 'pokemon',
            quantity: 1,
            purchase_date: tradeDateOnly,
            cost_basis_cents: ti.estimatedValueCents || null,
            visibility: 'private' as const,
            is_listed: false,
            is_negotiable: false,
            accepts_trades: false,
            ships_anywhere: false,
          }));

          const { data: createdItems, error: createError } = await supabase
            .from('user_items')
            .insert(newInventoryItems)
            .select('id');

          if (createError) throw createError;

          // Link created items back to trade_items records
          if (createdItems && insertedTradeItems) {
            // Find the matching trade_item records for external received items
            const externalReceivedTradeItems = insertedTradeItems.filter(
              ti => ti.direction === 'received' && ti.external_title && !ti.item_id
            );

            // Update trade_items with created_item_id
            for (let i = 0; i < Math.min(createdItems.length, externalReceivedTradeItems.length); i++) {
              await supabase
                .from('trade_items')
                .update({ created_item_id: createdItems[i].id })
                .eq('id', externalReceivedTradeItems[i].id);
            }
          }
        }
      }

      // Mark the main item as traded (sold)
      const { error: mainItemError } = await supabase
        .from('user_items')
        .update({
          sold_at: tradeDateIso,
          sold_price_cents: totalReceivedCents || null,
          visibility: 'private',
          is_listed: false,
          quantity: 0,
        })
        .eq('id', itemId)
        .eq('user_id', user.id);

      if (mainItemError) throw mainItemError;

      // Mark any additional "gave" items from inventory as traded
      const gaveItemIds = tradeItems
        .filter(t => t.direction === 'gave' && t.itemId)
        .map(t => t.itemId as string);

      if (gaveItemIds.length > 0) {
        const { error: gaveError } = await supabase
          .from('user_items')
          .update({
            sold_at: tradeDateIso,
            visibility: 'private',
            is_listed: false,
            quantity: 0,
          })
          .in('id', gaveItemIds)
          .eq('user_id', user.id);

        if (gaveError) throw gaveError;
      }

      // Count items added to inventory for toast message
      const itemsAddedToInventory = tradeItems.filter(
        ti => ti.direction === 'received' && ti.addToInventory && !ti.itemId
      ).length;

      return { transaction, tradeItems, itemsAddedToInventory };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-item'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-transactions'] });
    },
  });
}

interface SplitItemInput {
  itemId: string;
  /**
   * Optional map of `user_item_images.id` → target copy index.
   * 0 = original item, 1..N-1 = newly created drafts (in insert order).
   * Images not present in the map stay on the original.
   * The proof-of-possession image is always forced to index 0 regardless of input.
   */
  imageAssignments?: Record<string, number>;
}

export function useSplitItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, imageAssignments }: SplitItemInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the item to split
      const { data: item, error: fetchError } = await supabase
        .from('user_items')
        .select('*')
        .eq('id', itemId)
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;
      if (!item) throw new Error('Item not found');

      const quantity = item.quantity || 1;
      if (quantity <= 1) {
        throw new Error('Cannot split an item with quantity 1 or less');
      }

      // Get individual costs if available
      const individualCosts = item.individual_costs_cents as number[] | null;
      const hasIndividualCosts = individualCosts && individualCosts.length >= quantity;
      
      // Calculate cost basis per unit (use individual costs or divide evenly)
      const getCostForIndex = (idx: number): number | null => {
        if (hasIndividualCosts) {
          return individualCosts[idx] ?? null;
        }
        return item.cost_basis_cents 
          ? Math.round(item.cost_basis_cents / quantity) 
          : null;
      };

      // Create (quantity - 1) new items
      const newItems = [];
      for (let i = 1; i < quantity; i++) { // Start at 1, index 0 stays with original
        // Strip identity + per-unit numeric fields; trust/proof fields carry
        // over via ...itemData so each copy inherits the original's proof.
        const {
          id,
          created_at,
          updated_at,
          images,
          individual_costs_cents,
          individual_dates,
          short_id,
          sold_at,
          ...itemData
        } = item as any;
        newItems.push({
          ...itemData,
          quantity: 1,
          cost_basis_cents: getCostForIndex(i),
          // Reset listing status - user needs to set price for each
          visibility: 'private',
          is_listed: false,
          asking_price_cents: null,
          listed_at: null,
          expires_at: null,
          // Gallery photos + stock_image_url + proof_images/has_proof_pack
          // all inherit from original via ...itemData spread.
        });
      }

      // Insert new items
      const { data: createdItems, error: insertError } = await supabase
        .from('user_items')
        .insert(newItems)
        .select();

      if (insertError) throw insertError;

      const createdIds = createdItems?.map((c) => c.id) ?? [];

      // ─── Gallery photos ───────────────────────────────────────────────────
      // Default: each copy inherits a duplicate of every gallery image
      // (including the proof photo) from the original.
      // If the caller supplied `imageAssignments`, honor that MOVE-based
      // flow instead so the reorder sheet keeps working.
      const { data: allImages, error: imgFetchError } = await supabase
        .from('user_item_images')
        .select('id, image_url, display_order')
        .eq('item_id', itemId)
        .order('display_order', { ascending: true });
      if (imgFetchError) throw imgFetchError;

      if (imageAssignments && Object.keys(imageAssignments).length > 0) {
        // Group reassignments by target item id.
        const moves = new Map<string, string[]>(); // targetItemId -> imageIds
        for (const [imageId, copyIdx] of Object.entries(imageAssignments)) {
          if (copyIdx <= 0 || copyIdx > createdIds.length) continue;
          const targetId = createdIds[copyIdx - 1];
          if (!targetId) continue;
          const list = moves.get(targetId) ?? [];
          list.push(imageId);
          moves.set(targetId, list);
        }

        // Apply moves + reset display_order per target.
        for (const [targetId, imageIds] of moves.entries()) {
          for (let i = 0; i < imageIds.length; i++) {
            const { error: moveError } = await supabase
              .from('user_item_images')
              .update({ item_id: targetId, display_order: i })
              .eq('id', imageIds[i]);
            if (moveError) throw moveError;
          }
        }

        // Re-sequence remaining images on the original so display_order is contiguous.
        const movedIds = new Set(Array.from(moves.values()).flat());
        const remaining = (allImages ?? []).filter((img) => !movedIds.has(img.id));
        for (let i = 0; i < remaining.length; i++) {
          const { error: reseqError } = await supabase
            .from('user_item_images')
            .update({ display_order: i })
            .eq('id', remaining[i].id);
          if (reseqError) throw reseqError;
        }
      } else if (createdIds.length > 0 && allImages && allImages.length > 0) {
        // Default: each new copy inherits duplicates of every gallery image
        // from the original. Original keeps its rows untouched.
        const dupRows = createdIds.flatMap((newItemId) =>
          allImages.map((img, idx) => ({
            item_id: newItemId,
            image_url: img.image_url,
            display_order: idx,
          })),
        );
        const { error: dupError } = await supabase
          .from('user_item_images')
          .insert(dupRows);
        if (dupError) throw dupError;
      }



      // Get cost for original item (index 0)
      const individualCostsArray = item.individual_costs_cents as number[] | null;
      const hasIndividualCostsForOriginal = individualCostsArray && individualCostsArray.length > 0;
      const originalCost = hasIndividualCostsForOriginal 
        ? individualCostsArray[0] 
        : (item.cost_basis_cents ? Math.round(item.cost_basis_cents / quantity) : null);

      // Update original item to quantity 1
      const { error: updateError } = await supabase
        .from('user_items')
        .update({
          quantity: 1,
          cost_basis_cents: originalCost,
          individual_costs_cents: null, // Clear array - data is denormalized
          // Reset listing status for original too
          visibility: 'private',
          is_listed: false,
          asking_price_cents: null,
          listed_at: null,
          expires_at: null,
        })
        .eq('id', itemId)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      return {
        originalItemId: itemId,
        newItemIds: createdItems?.map(i => i.id) || [],
        totalItems: quantity,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['user-item'] });
    },
  });
}

export interface TradeItemWithDetails {
  id: string;
  direction: 'received' | 'gave';
  item_id: string | null;
  external_title: string | null;
  external_estimated_value_cents: number | null;
  created_item_id: string | null;
  inventory_item?: {
    id: string;
    title: string;
    stock_image_url: string | null;
  } | null;
  created_inventory_item?: {
    id: string;
    title: string;
    stock_image_url: string | null;
  } | null;
}

export interface TransactionWithDetails extends Transaction {
  item: {
    id: string;
    title: string;
    cost_basis_cents: number | null;
    quantity: number;
    stock_image_url: string | null;
    images: { image_url: string; display_order: number }[];
  } | null;
  trade_items: TradeItemWithDetails[];
}

export function useMyTransactions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['my-transactions', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          item:user_items(id, title, cost_basis_cents, quantity, stock_image_url, images:user_item_images(image_url, display_order)),
          trade_items(
            id, 
            direction, 
            item_id, 
            external_title, 
            external_estimated_value_cents,
            created_item_id
          )
        `)
        .or(`seller_id.eq.${user.id},buyer_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // The nested relationships for trade_items are complex due to multiple FKs
      // We'll handle this with a separate query if needed, or cast with 'as any'
      return data as unknown as TransactionWithDetails[];
    },
    enabled: !!user,
  });
}

// Update transaction mutation
interface UpdateTransactionInput {
  transactionId: string;
  amountCents?: number;
  completedAt?: string;
  paymentMethod?: string;
  salePhotoUrl?: string | null;
  tradeCashReceivedCents?: number;
  tradeCashPaidCents?: number;
  tradeNotes?: string | null;
  // For recalculating tax fields
  costBasisCentsSnapshot?: number | null;
  acquisitionDateSnapshot?: string | null;
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateTransactionInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Build update object with only provided fields
      const updateData: Record<string, unknown> = {};
      
      // Handle cost basis updates
      if (input.costBasisCentsSnapshot !== undefined) {
        updateData.cost_basis_cents_snapshot = input.costBasisCentsSnapshot;
      }
      
      // Update amount
      if (input.amountCents !== undefined) {
        updateData.amount_cents = input.amountCents;
      }
      
      // Recalculate realized gain whenever amount or cost basis is provided
      if (input.amountCents !== undefined || input.costBasisCentsSnapshot !== undefined) {
        const finalAmount = input.amountCents;
        const finalCostBasis = input.costBasisCentsSnapshot;
        if (finalAmount !== undefined && finalCostBasis != null) {
          updateData.realized_gain_cents = finalAmount - finalCostBasis;
        }
      }
      
      // Update completed_at and recalculate holding period
      if (input.completedAt !== undefined) {
        updateData.completed_at = input.completedAt;
        
        // Recalculate holding period if we have acquisition date
        if (input.acquisitionDateSnapshot) {
          const acquisitionDate = new Date(input.acquisitionDateSnapshot);
          const saleDate = new Date(input.completedAt);
          const holdingDays = Math.floor(
            (saleDate.getTime() - acquisitionDate.getTime()) / (1000 * 60 * 60 * 24)
          );
          updateData.holding_period = holdingDays > 365 ? 'long_term' : 'short_term';
        }
      }
      
      if (input.paymentMethod !== undefined) {
        updateData.payment_method = input.paymentMethod;
      }
      
      if (input.salePhotoUrl !== undefined) {
        updateData.sale_photo_url = input.salePhotoUrl;
      }
      
      if (input.tradeCashReceivedCents !== undefined) {
        updateData.trade_cash_received_cents = input.tradeCashReceivedCents;
      }
      
      if (input.tradeCashPaidCents !== undefined) {
        updateData.trade_cash_paid_cents = input.tradeCashPaidCents;
      }
      
      if (input.tradeNotes !== undefined) {
        updateData.trade_notes = input.tradeNotes;
      }
      
      // Only update if there are changes
      if (Object.keys(updateData).length === 0) {
        throw new Error('No changes to save');
      }
      
      const { data, error } = await supabase
        .from('transactions')
        .update(updateData as any)
        .eq('id', input.transactionId)
        .eq('seller_id', user.id) // Only seller can edit
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-transactions'] });
    },
  });
}

export function useLeaveReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      reviewedUserId,
      rating,
      comment,
      tags,
    }: {
      transactionId: string;
      reviewedUserId: string;
      rating: number;
      comment?: string;
      tags?: string[];
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('reviews')
        .insert({
          transaction_id: transactionId,
          reviewer_id: user.id,
          reviewed_user_id: reviewedUserId,
          rating,
          comment: comment || null,
          tags: tags || [],
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review-tag-counts'] });
      queryClient.invalidateQueries({ queryKey: ['my-transactions'] });
    },
  });
}

export function useUserReviews(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-reviews', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          reviewer:profiles!reviews_reviewer_id_fkey(user_id, username, avatar_url)
        `)
        .eq('reviewed_user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as (Review & { reviewer: any })[];
    },
    enabled: !!userId,
  });
}

export function usePendingReviews() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['pending-reviews', user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Get completed transactions where user is buyer and hasn't reviewed yet
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          item:user_items(id, title, images:user_item_images(image_url, display_order)),
          existing_review:reviews(id)
        `)
        .eq('buyer_id', user.id)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (error) throw error;

      // Filter to only transactions without a review from this user
      return (data || []).filter(tx => 
        !tx.existing_review?.length || 
        !tx.existing_review.some((r: any) => r.reviewer_id === user.id)
      );
    },
    enabled: !!user,
  });
}
